import React, { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const EditDiagnosis = () => {
  const { diagnosis_id } = useParams();
  const navigate = useNavigate();

  const [diagnosisName, setDiagnosisName] = useState("");
  const [symptom, setSymptom] = useState("");
  const [symptoms, setSymptoms] = useState([]); // { id?, symptom }
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  // Fetch diagnosis data
  useEffect(() => {
    const fetchCategories = () => {
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = `https://newerp.awasthivitality.com/api/category/list${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      fetch(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.data) setCategories(data.data);
        })
        .catch(() => {});
    };

    const fetchDiagnosis = () => {
      if (!diagnosis_id) return setLoading(false);
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = `https://newerp.awasthivitality.com/api/diagnosis/get/${diagnosis_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      fetch(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data) {
            setDiagnosisName(data.data.name);
            if (data.data.diagnosis_categoryid) {
              setSelectedCategoryId(String(data.data.diagnosis_categoryid));
            }
            setSymptoms(
              (data.data.symptoms || []).map((s) => ({
                id: s.diagnosis_symptom_id,
                symptom: s.symptom,
              }))
            );
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching diagnosis:", err);
          setLoading(false);
        });
    };

    fetchCategories();
    fetchDiagnosis();
  }, [diagnosis_id]);

  // Add new symptom
  const addSymptom = () => {
    if (symptom.trim()) {
      setSymptoms([...symptoms, { symptom: symptom.trim() }]); // new symptom has no id
      setSymptom("");
    }
  };

  // Remove symptom (calls delete API for existing ones)
  const removeSymptom = (index) => {
    const symptomToRemove = symptoms[index];

    if (symptomToRemove.id) {
      // Delete from API
      fetch(`https://newerp.awasthivitality.com/api/diagnosis/symptom/delete/${symptomToRemove.id}`, {
        method: "DELETE",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            const updated = [...symptoms];
            updated.splice(index, 1);
            setSymptoms(updated);
          } else {
            alert("Failed to delete symptom");
          }
        })
        .catch((err) => {
          console.error("Error deleting symptom:", err);
          alert("Failed to delete symptom");
        });
    } else {
      // Remove locally for new symptom
      const updated = [...symptoms];
      updated.splice(index, 1);
      setSymptoms(updated);
    }
  };

  // Update diagnosis
  const handleSubmit = () => {
    if (!diagnosisName || symptoms.length === 0) {
      alert("Please enter diagnosis name and at least one symptom.");
      return;
    }

    if (!selectedCategoryId) {
      alert('Please select a category.');
      return;
    }

    const symptomsList = symptoms.map((s) => s.symptom);
    const symptomsString = JSON.stringify(symptomsList);

    const token = (localStorage.getItem('session_token') || '').trim();
    const url = `https://newerp.awasthivitality.com/api/diagnosis/update/${diagnosis_id}?name=${encodeURIComponent(
      diagnosisName
    )}&diagnosis_id=${diagnosis_id}&symptoms=${encodeURIComponent(symptomsString)}&diagnosis_categoryid=${encodeURIComponent(selectedCategoryId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
    fetch(url, {
      method: "PUT",
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(token ? { 'X-Session-Token': token } : {}),
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Diagnosis updated successfully!");
          navigate("/diagnoses");
        } else {
          alert("Failed to update diagnosis");
        }
      })
      .catch((err) => {
        console.error("Error updating diagnosis:", err);
        alert("Failed to update diagnosis.");
      });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div
      style={{
        padding: "2rem",
        background: "#f4f5f7",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", marginLeft: "230px" }}>
        <div
          style={{
            background: "#fff",
            borderRadius: "6px",
            overflow: "hidden",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "rgb(0 195 225)",
              color: "white",
              padding: "12px 16px",
              fontWeight: "bold",
            }}
          >
            Edit Diagnose
          </div>

          <div style={{ padding: "16px" }}>
            {/* Diagnosis Name */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Diagnosis Name</label>
              <input
                type="text"
                value={diagnosisName}
                onChange={(e) => setDiagnosisName(e.target.value)}
                placeholder="Enter Diagnosis Name"
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Category */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Category</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.category_id || cat.id} value={cat.category_id || cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Symptoms */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Sign & Symptoms</label>
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <input
                  type="text"
                  value={symptom}
                  onChange={(e) => setSymptom(e.target.value)}
                  placeholder="Enter Sign & Symptoms"
                  style={{
                    flex: 1,
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
                <button
                  onClick={addSymptom}
                  style={{
                    background: "rgb(0 195 225)",
                    border: "none",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Add More
                </button>
              </div>
            </div>

            {/* Symptom Details */}
            <div
              style={{
                marginTop: "1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "rgb(0 195 225)",
                  color: "white",
                  padding: "10px",
                  fontWeight: "bold",
                }}
              >
                Sign & Symptom Details
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>S No</th>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>Sign & Symptom</th>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {symptoms.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ textAlign: "center", padding: "12px", color: "#666" }}>
                        No symptoms added
                      </td>
                    </tr>
                  ) : (
                    symptoms.map((s, i) => (
                      <tr key={i}>
                        <td style={{ padding: "8px" }}>{i + 1}</td>
                        <td style={{ padding: "8px" }}>{s.symptom}</td>
                        <td style={{ padding: "8px" }}>
                          <button
                            onClick={() => removeSymptom(i)}
                            style={{ background: "transparent", border: "none", cursor: "pointer", color: "red" }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Submit */}
            <button
              style={{
                marginTop: "1rem",
                padding: "10px 20px",
                background: "rgb(0 195 225)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={handleSubmit}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDiagnosis;
